import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Rocket, Power, Calendar, Eye, Radio, Mail, MessageSquare,
  Users, ShieldCheck, RefreshCw, Send, Trash2, Download,
  AlertCircle, CheckCircle2, Clock, Search, ArrowUpRight,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar,
} from "recharts";
import { toast } from "sonner";

/* ---------- helpers ---------- */
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium", timeStyle: "short",
    });
  } catch { return iso; }
}
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/* ---------- Section wrapper ---------- */
function Section({ title, subtitle, icon: Icon, children, actions, testid }) {
  return (
    <section className="card-surface p-5 sm:p-6" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-[var(--bull)]" />}
            <h2 className="text-white font-semibold text-[15px] tracking-tight">{title}</h2>
          </div>
          {subtitle && <div className="text-white/45 text-[12px] mt-1">{subtitle}</div>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}

/* ---------- Toggle ---------- */
function Toggle({ checked, onChange, testid, label, hint }) {
  return (
    <label className="flex items-center justify-between gap-3 py-2.5 border-b border-white/[0.06] last:border-0 cursor-pointer">
      <div>
        <div className="text-white text-[13px] tracking-tight">{label}</div>
        {hint && <div className="text-white/40 text-[11px] mt-0.5">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`w-11 h-6 rounded-full border transition-all relative shrink-0 ${
          checked
            ? "bg-[#00ff9c] border-[#00ff9c]"
            : "bg-white/[0.04] border-white/15"
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all ${
            checked ? "left-[22px]" : "left-0.5"
          }`}
          style={checked ? {} : { background: "rgba(255,255,255,0.9)" }}
        />
      </button>
    </label>
  );
}

/* ---------- KPI card ---------- */
function Kpi({ label, value, sub, accent = "var(--bull)", icon: Icon, testid }) {
  return (
    <div className="card-surface p-4 relative overflow-hidden" data-testid={testid}>
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: `radial-gradient(circle at 100% 0%, ${accent}22, transparent 60%)` }} />
      <div className="relative">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
          <div className="label-mono">{label}</div>
        </div>
        <div className="font-mono text-2xl text-white font-semibold tabular-nums mt-1 tracking-tight">
          {typeof value === "number" ? value.toLocaleString() : (value ?? "—")}
        </div>
        {sub && <div className="text-[11px] text-white/45 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

/* ---------- Waitlist Manager ---------- */
function WaitlistManager() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("created_at:desc");
  const [page, setPage] = useState(1);
  const [perPage] = useState(25);
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort, page: String(page), per_page: String(perPage),
      });
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      const { data } = await api.get(`/api/admin/launch/waitlist?${params.toString()}`);
      setData(data);
    } finally { setLoading(false); }
  }, [q, status, sort, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const del = async (wid) => {
    if (!window.confirm("Delete this waitlist signup permanently?")) return;
    setBusy(true);
    try {
      await api.delete(`/api/admin/launch/waitlist/${wid}`);
      toast.success("Signup deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
    finally { setBusy(false); }
  };

  const download = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/launch/waitlist.csv`;
    const a = document.createElement("a");
    a.href = url;
    a.click();
  };

  const totalPages = Math.max(1, Math.ceil(data.total / perPage));

  return (
    <Section
      title="Waitlist Manager"
      subtitle="Search, review, and export every signup."
      icon={Users}
      testid="waitlist-manager"
      actions={
        <div className="flex items-center gap-2">
          <button
            data-testid="waitlist-refresh"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-[12px] text-white/70 hover:text-white px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/[0.05] transition">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            data-testid="waitlist-export-csv"
            onClick={download}
            className="inline-flex items-center gap-1.5 text-[12px] text-black font-semibold bg-white hover:bg-white/90 px-3 py-1.5 rounded-lg transition">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      }
    >
      {/* filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="waitlist-search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search name, email, phone, campaign…"
            className="w-full rounded-lg bg-white/[0.03] border border-white/10 pl-9 pr-3 py-2 text-[13px] text-white placeholder-white/30 focus:outline-none focus:border-white/40 transition"
          />
        </div>
        <select
          data-testid="waitlist-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-[13px] text-white/80"
        >
          <option value="">All statuses</option>
          <option value="verified">Verified</option>
          <option value="pending_verification">Pending</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="blocked">Blocked</option>
        </select>
        <select
          data-testid="waitlist-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-[13px] text-white/80"
        >
          <option value="created_at:desc">Newest first</option>
          <option value="created_at:asc">Oldest first</option>
          <option value="verified_at:desc">Recently verified</option>
          <option value="email:asc">Email A→Z</option>
        </select>
      </div>

      {/* table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-white/45 border-b border-white/10">
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px]">Name / Email</th>
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px]">Phone</th>
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px]">Status</th>
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px]">Source</th>
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px]">Country</th>
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px]">Joined</th>
              <th className="py-2 pr-3 font-mono uppercase tracking-[0.15em] text-[10px] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="py-6 text-center text-white/40">Loading…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-white/40">No signups match.</td></tr>
            )}
            {!loading && data.items.map((r) => (
              <tr key={r.waitlist_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                <td className="py-2.5 pr-3">
                  <div className="text-white">{r.name || "—"}</div>
                  <div className="text-white/50 text-[11.5px]">{r.email}</div>
                </td>
                <td className="py-2.5 pr-3 text-white/70 font-mono text-[11.5px]">
                  {r.phone || "—"}{r.phone_verified && <span className="ml-1 text-[#00ff9c]">✓</span>}
                </td>
                <td className="py-2.5 pr-3">
                  <StatusBadge status={r.status} />
                </td>
                <td className="py-2.5 pr-3 text-white/70">
                  {r.utm_source || "direct"}
                  {r.utm_campaign && <span className="text-white/40"> · {r.utm_campaign}</span>}
                </td>
                <td className="py-2.5 pr-3 text-white/60 font-mono">{r.country || "—"}</td>
                <td className="py-2.5 pr-3 text-white/60">{fmtDate(r.created_at)}</td>
                <td className="py-2.5 pr-3 text-right">
                  <button
                    data-testid={`waitlist-delete-${r.waitlist_id}`}
                    disabled={busy}
                    onClick={() => del(r.waitlist_id)}
                    className="inline-flex items-center gap-1 text-[11.5px] text-red-400 hover:text-red-300 px-2 py-1 rounded border border-red-500/20 hover:border-red-500/40 transition">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* pagination */}
      <div className="flex items-center justify-between mt-3 text-[12px] text-white/50">
        <div>{data.total.toLocaleString()} signups</div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                  className="px-2 py-1 rounded border border-white/10 disabled:opacity-30 hover:bg-white/[0.04]"
                  data-testid="waitlist-prev-page">Prev</button>
          <div className="font-mono">{page}/{totalPages}</div>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                  className="px-2 py-1 rounded border border-white/10 disabled:opacity-30 hover:bg-white/[0.04]"
                  data-testid="waitlist-next-page">Next</button>
        </div>
      </div>
    </Section>
  );
}

function StatusBadge({ status }) {
  const map = {
    verified: { c: "#00ff9c", label: "Verified" },
    pending_verification: { c: "#ffd166", label: "Pending" },
    unsubscribed: { c: "#7aa2ff", label: "Unsubscribed" },
    blocked: { c: "#ff4d4f", label: "Blocked" },
    duplicate: { c: "rgba(255,255,255,0.4)", label: "Duplicate" },
    invalid: { c: "rgba(255,255,255,0.4)", label: "Invalid" },
    launch_notified: { c: "#00ff9c", label: "Notified" },
  };
  const s = map[status] || { c: "rgba(255,255,255,0.4)", label: status || "—" };
  return (
    <span
      className="inline-block px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-[0.1em]"
      style={{ color: s.c, background: `${s.c}18`, border: `1px solid ${s.c}33` }}
    >
      {s.label}
    </span>
  );
}

/* ---------- Analytics ---------- */
function Analytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/api/admin/launch/analytics?days=${days}`);
      setData(data);
    })();
  }, [days]);

  const daily = data?.signups_by_day || [];
  const sources = data?.by_source || [];
  const devices = data?.by_device || [];
  const campaigns = data?.by_campaign || [];

  return (
    <Section
      title="Waitlist Analytics"
      subtitle="Verified signups, sources, and campaign performance."
      icon={Radio}
      testid="waitlist-analytics"
      actions={
        <div className="inline-flex items-center bg-white/[0.04] border border-white/10 rounded-full p-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`analytics-window-${d}d`}
              className={`px-3 py-1.5 rounded-full text-[11.5px] font-mono tracking-widest transition-all ${
                days === d ? "bg-white text-black" : "text-white/65 hover:text-white"
              }`}
            >
              {d}D
            </button>
          ))}
        </div>
      }
    >
      <div className="h-[240px] mb-5">
        <ResponsiveContainer>
          <AreaChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <defs>
              <linearGradient id="wlV" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00ff9c" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#00ff9c" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="wlT" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7aa2ff" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#7aa2ff" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="day" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }}
                   tickFormatter={(d) => d.slice(5)} stroke="rgba(255,255,255,0.06)" />
            <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
            <Tooltip
              contentStyle={{
                background: "rgba(10,10,12,0.95)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 10, fontSize: 12,
              }}
            />
            <Area type="monotone" dataKey="verified" name="Verified" stroke="#00ff9c" strokeWidth={1.8} fill="url(#wlV)" />
            <Area type="monotone" dataKey="total" name="Total" stroke="#7aa2ff" strokeWidth={1.4} fill="url(#wlT)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-white/70 text-[12px] font-semibold mb-2 tracking-tight">Top sources</div>
          <ul className="space-y-1.5">
            {sources.length === 0 && <li className="text-white/40 text-[12px]">No source data yet.</li>}
            {sources.map((s) => (
              <li key={s.source} className="flex items-center justify-between text-[12.5px]">
                <span className="text-white/80">{s.source}</span>
                <span className="font-mono text-white/90 tabular-nums">
                  {s.verified.toLocaleString()}<span className="text-white/40"> / {s.total.toLocaleString()}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-white/70 text-[12px] font-semibold mb-2 tracking-tight">Top campaigns</div>
          <ul className="space-y-1.5">
            {campaigns.length === 0 && <li className="text-white/40 text-[12px]">No campaign data yet.</li>}
            {campaigns.map((s) => (
              <li key={s.campaign} className="flex items-center justify-between text-[12.5px]">
                <span className="text-white/80">{s.campaign}</span>
                <span className="font-mono text-white/90 tabular-nums">
                  {s.verified.toLocaleString()}<span className="text-white/40"> / {s.total.toLocaleString()}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2 h-[160px]">
          <div className="text-white/70 text-[12px] font-semibold mb-2 tracking-tight">Device breakdown</div>
          {devices.length === 0 ? (
            <div className="text-white/40 text-[12px]">No data.</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={devices} margin={{ top: 8, right: 0, left: -22, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="device" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} stroke="rgba(255,255,255,0.06)" />
                <Tooltip contentStyle={{ background: "rgba(10,10,12,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, fontSize: 12 }} />
                <Bar dataKey="count" fill="#00ff9c" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ---------- Broadcaster ---------- */
function LaunchBroadcast() {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/api/admin/launch/notifications/summary");
    setSummary(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async (channel, resend_failed = false) => {
    if (!window.confirm(
      resend_failed
        ? `Retry failed ${channel === "both" ? "email + SMS" : channel} launch notifications now?`
        : `Send launch ${channel === "both" ? "emails + SMS" : channel} to every verified subscriber now? This cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/admin/launch/notifications/broadcast", {
        channel, resend_failed, dry_run: false,
      });
      toast.success(
        `Queued: ${data.queued.toLocaleString()} — email sent ${data.email_sent}, sms sent ${data.sms_sent}`,
      );
      load();
    } catch (e) {
      toast.error("Broadcast failed");
    } finally { setBusy(false); }
  };

  return (
    <Section
      title="Launch Notifications"
      subtitle="Send launch email & SMS to every verified subscriber. Duplicates are automatically prevented."
      icon={Send}
      testid="launch-broadcast"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi icon={Users} label="Total waiting" value={summary?.total_waiting} testid="notif-total-waiting" />
        <Kpi icon={Mail} label="Emails ready" value={summary?.email_ready} accent="#7aa2ff" testid="notif-email-ready" />
        <Kpi icon={MessageSquare} label="SMS ready" value={summary?.sms_ready} accent="#ffd166" testid="notif-sms-ready" />
        <Kpi icon={CheckCircle2} label="Email sent" value={summary?.email?.sent} testid="notif-email-sent" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi icon={Clock} label="Email pending" value={summary?.email?.pending} accent="#ffd166" testid="notif-email-pending" />
        <Kpi icon={AlertCircle} label="Email failed" value={summary?.email?.failed} accent="#ff4d4f" testid="notif-email-failed" />
        <Kpi icon={CheckCircle2} label="SMS sent" value={summary?.sms?.sent} testid="notif-sms-sent" />
        <Kpi icon={AlertCircle} label="SMS failed" value={summary?.sms?.failed} accent="#ff4d4f" testid="notif-sms-failed" />
      </div>

      {!summary?.email_configured && (
        <div className="text-[11.5px] text-white/50 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 mb-3">
          Email delivery is currently stubbed — set <span className="font-mono text-white/70">RESEND_API_KEY</span> or SMTP env vars to activate real delivery. Broadcasts still log & count in the meantime.
        </div>
      )}
      {!summary?.sms_configured && (
        <div className="text-[11.5px] text-white/50 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 mb-3">
          Twilio is not configured — SMS broadcasts will be recorded as stubbed until <span className="font-mono text-white/70">TWILIO_*</span> env vars are set.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          data-testid="broadcast-both"
          disabled={busy}
          onClick={() => send("both", false)}
          className="inline-flex items-center gap-2 rounded-lg bg-white text-black font-semibold text-[13px] px-4 py-2.5 hover:bg-white/90 transition disabled:opacity-60">
          <Send className="w-4 h-4" /> Send Launch Notifications
        </button>
        <button
          data-testid="broadcast-email"
          disabled={busy}
          onClick={() => send("email", false)}
          className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] text-white text-[13px] px-4 py-2.5 hover:bg-white/[0.06] transition disabled:opacity-60">
          <Mail className="w-4 h-4" /> Email only
        </button>
        <button
          data-testid="broadcast-sms"
          disabled={busy}
          onClick={() => send("sms", false)}
          className="inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/[0.03] text-white text-[13px] px-4 py-2.5 hover:bg-white/[0.06] transition disabled:opacity-60">
          <MessageSquare className="w-4 h-4" /> SMS only
        </button>
        <button
          data-testid="broadcast-retry-failed"
          disabled={busy}
          onClick={() => send("both", true)}
          className="inline-flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.06] text-red-300 text-[13px] px-4 py-2.5 hover:bg-red-500/[0.12] transition disabled:opacity-60">
          <RefreshCw className="w-4 h-4" /> Retry Failed
        </button>
      </div>
    </Section>
  );
}

/* ---------- Main Page ---------- */
export default function LaunchControl() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (user && !isAdmin) navigate("/dashboard", { replace: true });
  }, [user, isAdmin, navigate]);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/api/admin/launch/config"),
        api.get("/api/admin/launch/summary"),
      ]);
      setCfg(c.data);
      setSummary(s.data);
    } catch (e) { /* no-op */ }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/api/admin/launch/config", patch);
      setCfg(data);
      toast.success("Launch settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!isAdmin) return null;

  const totals = summary?.analytics?.totals || {};

  return (
    <div className="min-h-screen flex flex-col" data-testid="launch-control-page">
      <Header />
      <main id="main-content" className="flex-1 max-w-[1400px] w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="label-mono">FlowFinder · launch control center</div>
            <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight leading-tight mt-1">
              Launch Control
            </h1>
            <div className="text-white/45 text-sm mt-1">
              Coming Soon Mode is <span className={cfg?.coming_soon_enabled ? "text-[#00ff9c] font-semibold" : "text-white/70"}>
                {cfg?.coming_soon_enabled ? "ON" : "OFF"}
              </span>. Non-admin visitors {cfg?.coming_soon_enabled ? "see the Coming Soon landing page." : "see the live FlowFinder app."}
            </div>
          </div>
          <a
            href="/admin"
            className="inline-flex items-center gap-1.5 text-[12px] text-white/65 hover:text-white px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/[0.05] transition"
          >
            ← Back to Admin
          </a>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          <Kpi icon={Users} label="Verified" value={totals.verified} testid="lc-kpi-verified" />
          <Kpi icon={Clock} label="Pending" value={totals.pending} accent="#ffd166" testid="lc-kpi-pending" />
          <Kpi icon={CheckCircle2} label="Today" value={totals.today} accent="#7aa2ff" testid="lc-kpi-today" />
          <Kpi icon={Radio} label="Week" value={totals.week} accent="#7aa2ff" testid="lc-kpi-week" />
          <Kpi icon={Radio} label="Month" value={totals.month} accent="#7aa2ff" testid="lc-kpi-month" />
          <Kpi icon={ShieldCheck} label="Verify rate" value={totals.verification_rate != null ? `${totals.verification_rate}%` : "—"} testid="lc-kpi-rate" />
        </div>

        {/* Controls */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
          <Section title="Coming Soon Mode" icon={Power} subtitle="Master switch. Turn OFF to instantly show the full FlowFinder app again." testid="mode-toggle-section">
            <Toggle
              testid="toggle-coming-soon"
              label="Coming Soon Mode"
              hint="Non-admins see the coming-soon landing page."
              checked={!!cfg?.coming_soon_enabled}
              onChange={(v) => save({ coming_soon_enabled: v })}
            />
            <Toggle
              testid="toggle-auto-launch"
              label="Automatically launch when countdown reaches zero"
              hint="Flips Coming Soon Mode OFF automatically at the launch time."
              checked={!!cfg?.auto_launch_on_countdown_zero}
              onChange={(v) => save({ auto_launch_on_countdown_zero: v })}
            />
            <Toggle
              testid="toggle-auto-email"
              label="Automatically send launch emails"
              hint="Fires launch email to verified subscribers when the countdown ends."
              checked={!!cfg?.auto_send_launch_email}
              onChange={(v) => save({ auto_send_launch_email: v })}
            />
            <Toggle
              testid="toggle-auto-sms"
              label="Automatically send launch SMS"
              hint="Fires launch SMS to verified phone subscribers when the countdown ends."
              checked={!!cfg?.auto_send_launch_sms}
              onChange={(v) => save({ auto_send_launch_sms: v })}
            />
          </Section>

          <Section title="Launch Date & Time" icon={Calendar} subtitle="Controls the countdown timer on the landing page." testid="launch-date-section">
            <label className="block text-[11.5px] font-mono uppercase tracking-[0.15em] text-white/45 mb-1.5">
              Launch at (your local time)
            </label>
            <input
              data-testid="launch-at-input"
              type="datetime-local"
              defaultValue={toLocalInput(cfg?.launch_at)}
              onBlur={(e) => {
                const iso = fromLocalInput(e.target.value);
                if (iso) save({ launch_at: iso });
              }}
              className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2.5 text-[13px] text-white focus:outline-none focus:border-white/40 transition"
            />
            <div className="mt-1.5 text-[11.5px] text-white/45">
              Current: <span className="font-mono text-white/70">{fmtDate(cfg?.launch_at)}</span>
            </div>

            <div className="mt-4 border-t border-white/[0.06] pt-4">
              <div className="text-[11.5px] font-mono uppercase tracking-[0.15em] text-white/45 mb-2">Preview</div>
              <div className="flex flex-wrap gap-2">
                <a
                  data-testid="preview-coming-soon"
                  href="/?preview=coming-soon"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[12px] text-white/80 hover:text-white px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/[0.05] transition">
                  <Eye className="w-3.5 h-3.5" /> Preview Coming Soon
                  <ArrowUpRight className="w-3 h-3" />
                </a>
                <a
                  data-testid="preview-live"
                  href="/?preview=live"
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[12px] text-white/80 hover:text-white px-3 py-1.5 rounded-lg border border-white/10 hover:bg-white/[0.05] transition">
                  <Eye className="w-3.5 h-3.5" /> Preview Live Website
                  <ArrowUpRight className="w-3 h-3" />
                </a>
              </div>
            </div>
          </Section>
        </div>

        {/* Broadcast */}
        <div className="mb-5">
          <LaunchBroadcast />
        </div>

        {/* Analytics */}
        <div className="mb-5">
          <Analytics />
        </div>

        {/* Waitlist manager */}
        <div className="mb-8">
          <WaitlistManager />
        </div>

        {saving && (
          <div className="text-[11px] text-white/45 font-mono">saving…</div>
        )}
      </main>
      <Footer />
    </div>
  );
}
